import { Divider, Modal, ModalProps, Switch } from 'antd';
import { Helmet } from 'react-helmet-async';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../../api/useAPIMutation';
import Form from '../../components/Form';
import { CandidateRegisterFieldDetailSerializer } from '../../types/api';

interface PCDAdditionalRegisterFieldsFormValues {
  cidVisible: boolean;
  cidRequired: boolean;
  pcdReportVisible: boolean;
}

interface PCDAdditionalRegisterFieldsModalProps extends ModalProps {
  cidRegisterField?: CandidateRegisterFieldDetailSerializer;
  pcdReportRegisterField?: CandidateRegisterFieldDetailSerializer;
  onSuccess?: () => void | Promise<unknown>;
}

const PCDAdditionalRegisterFieldsModal = ({
  cidRegisterField,
  pcdReportRegisterField,
  onSuccess,
  ...props
}: PCDAdditionalRegisterFieldsModalProps) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const mutation = useAPIMutation(
    async (values: PCDAdditionalRegisterFieldsFormValues, api) => {
      api.patch('/candidate-register-fields/cid/', {
        visible: values.cidVisible ? 'YES' : 'NO',
        required: values.cidRequired ? 'YES' : 'NO',
      });
      api.patch('/candidate-register-fields/pcd_report/', {
        visible: values.pcdReportVisible ? 'YES' : 'NO',
      });
    },
    {
      onSuccess,
      onSettled: async (_, __) => {
        await queryClient.invalidateQueries('/candidate-register-fields/');
      },
    }
  );

  return (
    <Modal
      onOk={() => form.submit()}
      okButtonProps={{ loading: mutation.isLoading }}
      title="Campos adicionais para PcD"
      width={700}
      {...props}
    >
      <Helmet>
        <title>
          Campos Adicionais PcD | Formulário de Inscrição | Configurações | Recrutamento e Seleção
        </title>
      </Helmet>
      <Form<PCDAdditionalRegisterFieldsFormValues>
        form={form}
        layout="vertical"
        initialValues={{
          cidVisible: ['ALWAYS', 'YES'].includes(cidRegisterField?.visible || ''),
          cidRequired: ['ALWAYS', 'YES'].includes(cidRegisterField?.required || ''),
          pcdReportVisible: ['ALWAYS', 'YES'].includes(pcdReportRegisterField?.visible || ''),
        }}
        onFinish={(variables) => {
          mutation.mutate(variables);
        }}
      >
        <Form.Item>
          <Form.Row>
            <Form.Col flex={1}>
              <Form.Legend>Dado</Form.Legend>
            </Form.Col>
            <Form.Col span={4}>
              <Form.Legend>Visível</Form.Legend>
            </Form.Col>
            <Form.Col span={4}>
              <Form.Legend>Obrigatório</Form.Legend>
            </Form.Col>
          </Form.Row>
          <Divider style={{ marginTop: '8px', marginBottom: '8px' }} />
          <Form.Row>
            <Form.Col flex={1}>Inserir CID</Form.Col>
            <Form.Item noStyle shouldUpdate={(prev, next) => prev.cidVisible !== next.cidVisible}>
              {(form) => (
                <>
                  <Form.Col span={4}>
                    <Form.Item name="cidVisible" valuePropName="checked" noStyle>
                      <Switch
                        onChange={(checked) => {
                          if (!checked) {
                            form.setFieldsValue({ cidRequired: false });
                          }
                        }}
                      />
                    </Form.Item>
                  </Form.Col>
                  <Form.Col span={4}>
                    <Form.Item name="cidRequired" valuePropName="checked" noStyle>
                      <Switch disabled={!form.getFieldValue('cidVisible')} />
                    </Form.Item>
                  </Form.Col>
                </>
              )}
            </Form.Item>
          </Form.Row>
          <Divider style={{ marginTop: '8px', marginBottom: '8px' }} />
          <Form.Row>
            <Form.Col flex={1}>Anexo do laudo</Form.Col>
            <Form.Col span={4}>
              <Form.Item name="pcdReportVisible" valuePropName="checked" noStyle>
                <Switch />
              </Form.Item>
            </Form.Col>
            <Form.Col span={4} />
          </Form.Row>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PCDAdditionalRegisterFieldsModal;
